// Link + isTTSSupported imports removed — the top toolbar no longer carries
// the back link or the play button (both moved to the bottom bar
// 2026-06-20). Kept the chapter-title slot for the optional reveal.

interface Props {
  title?: string;
  chapter?: string;
  /** Master "interface hidden" flag. When true, the toolbar contracts to
   *  just the safe-area-inset-top strip — no back arrow, no title, no
   *  play button — and the reading area grows to fill the space. A
   *  floating play button rendered elsewhere takes over Read-aloud while
   *  the toolbar is hidden. */
  hidden?: boolean;
  playing: boolean;
  paused: boolean;
  onPlay: () => void;
  onPause: () => void;
  onResume: () => void;
}

/**
 * Reader top bar — three slots:
 *   ← bold back-arrow on the left (no "Library" word)
 *   chapter title in the centre (hidden when interface is hidden)
 *   theme swatch + outlined-yellow Play button on the right
 *
 * No bottom border — the chrome should read as floating over the page,
 * not a hard divider line that breaks the calm reading mood.
 */
export function ReaderToolbar({
  title,
  chapter,
  hidden,
  playing,
  paused,
  onPlay,
  onPause,
  onResume,
}: Props) {
  // Back arrow and play/pause moved to the bottom bar 2026-06-20 so the
  // user has both within thumb reach. The top toolbar now just carries
  // the book/chapter title — and only when chromeVisible is true (tap
  // centre of the reading area to reveal). When chrome is hidden the
  // reader still respects the iOS notch via the safe-area inset.
  void playing; void paused; void onPlay; void onPause; void onResume;
  if (hidden) {
    // 2026-10-05, from the BOOX ("Make text full screen"): this strip used
    // to add 4px above and 4px below the safe-area inset, and 12px of
    // side padding, even with the interface hidden. On a device with no
    // notch — which is every e-reader — env(safe-area-inset-*) is 0, so
    // that was 8px of dead band across the top of the page and 24px of
    // horizontal inset that the text could not use. Hidden now means
    // hidden: reserve the physical inset and nothing else, so the
    // reading column actually reaches the edges of the panel.
    return (
      <div
        style={{
          backgroundColor: "var(--pv-bg)",
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: "0px",
          paddingLeft: "env(safe-area-inset-left, 0px)",
          paddingRight: "env(safe-area-inset-right, 0px)",
        }}
      />
    );
  }

  return (
    <div
      className="pv-fg flex items-center justify-center text-sm"
      style={{
        backgroundColor: "var(--pv-bg)",
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 8px)",
        paddingBottom: "8px",
        paddingLeft: "calc(env(safe-area-inset-left, 0px) + 12px)",
        paddingRight: "calc(env(safe-area-inset-right, 0px) + 12px)",
      }}
    >
      {/* 2026-10-05, from the BOOX ("Remove title too"): the book title is
          gone. He knows which book he opened; repeating it on every page
          is one more thing competing with the text. The chapter still
          earns its place — it tells him where he is — so when there is a
          chapter label we show that alone, and when there isn't we show
          nothing rather than falling back to the title. */}
      <div className="pv-fg-soft flex-1 truncate text-center text-lg">
        {chapter ?? ""}
      </div>
    </div>
  );
}

/** Simple grey play triangle / pause bars. Plain SVG, no emoji rendering
 *  quirks across iOS / Android / desktop. Inherits color from the button
 *  via currentColor so theme + parent color classes drive the look. */
export function PlayPauseGlyph({
  showPause,
  size = 14,
}: {
  showPause: boolean;
  size?: number;
}) {
  return showPause ? (
    <svg
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill="currentColor"
      aria-hidden
    >
      <rect x="3" y="2" width="3" height="10" rx="0.5" />
      <rect x="8" y="2" width="3" height="10" rx="0.5" />
    </svg>
  ) : (
    <svg
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill="currentColor"
      aria-hidden
    >
      <path d="M3.5 2 L11.5 7 L3.5 12 Z" />
    </svg>
  );
}
